import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import DocumentsSection from './components/DocumentSection/DocumentsSection.tsx';
import { ERROR_CODES, HTTP_ERROR_STATUS } from '~/constants/errorCodes.ts';
import { errorMessages } from '~/constants/errorMessages.ts';
import { useGetDocumentIdsSections } from './hooks/useGetDocumentIdsSections.ts';
import { useGetDocuments } from './hooks/useGetDocuments.ts';
import { ALLOWED_FILE_TYPES, MAX_FILE_SIZE, P7S_FILE_TYPES } from './constants.tsx';
import {
  addDocument,
  deleteDocument,
  deleteDocumentAssetManager,
  addDocumentAssetManager,
  previewDocument,
  previewAssetManagerDocument,
} from '~/store/identification-form/thunks/documentThunks.ts';
import { SignDocuments } from './components/SignDocuments/SignDocuments.tsx';
import useGetContragentTypes from '../../hooks/useGetContragentTypes.ts';
import { DocStatusEnum, TDocument, TDocumentsInStore } from './types.ts';
import { useAppDispatch } from '~/store/appDispatch.ts';
import useDocumentsToSign from './hooks/useDocumentsToSign.ts';
import useGetUserRole from '../../../../hooks/useGetUserRole.ts';
import { WebDocName } from '~/constants/docs.ts';
import { setIdentificationError } from '~/store/identification-form/slices.ts';
import { DeleteModal } from '~/components/modals/DeleteModal/DeleteModal.tsx';
import { useFeatureFlags } from '~/helpers/FeatureFlagsContext.tsx';
import { setUboOwnerMapEntry } from './utils.ts';
import {
  selectApplicationId,
  selectDocuments,
  selectIsReidentification,
} from '~/store/identification-form/selectors.ts';
import { selectTokenData } from '~/store/users/selectors.ts';
import { AxiosError, AxiosResponse } from 'axios';
import { commonStyles } from '~/theme/styles/commonStyles.ts';
import { Box, SxProps } from '@mui/material';

const Documents = () => {
  const dispatch = useAppDispatch();
  const { isAssetManager } = useGetUserRole();
  const { isOwnershipStructureTreeEnabled } = useFeatureFlags();
  const tokenData = useSelector(selectTokenData);
  const appId = useSelector(selectApplicationId);
  const formDataDocuments = useSelector(selectDocuments);
  const isReidentification = useSelector(selectIsReidentification);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentDocId, setCurrentDocId] = useState<{ blockId: number; idDoc: number } | null>(null);
  const [loadingDocId, setLoadingDocId] = useState<{ blockId: number; index: number } | null>(null);

  const applicationId = isAssetManager ? tokenData?.appId : appId;
  const { isLegalResident, isSoleTrader } = useGetContragentTypes();
  const {
    additionalAlwaysOptionalIds,
    additionalConditionallyRequiredIds,
    assetManagersDocumentsIds,
    mainConditionallyRequiredIds,
  } = useGetDocumentIdsSections();

  const { documents: additionalOptionalDocuments } = useGetDocuments(
    additionalAlwaysOptionalIds,
    (formDataDocuments as TDocumentsInStore).documents.additionalDocuments || [],
    {
      ...(isLegalResident && isOwnershipStructureTreeEnabled && !isReidentification
        ? { [WebDocName.ownershipStructureInfo]: true }
        : {}),
      ...(isLegalResident
        ? {
            [WebDocName.constituentDocumentAccessCodeDescription]: true,
            [WebDocName.financialAssistanceLoanAgreement]: true,
          }
        : {}),
      ...(isLegalResident || isSoleTrader
        ? { [WebDocName.leaseAgreement]: true, [WebDocName.contractRelatedDocuments]: true }
        : {}),
    }
  );
  const { documents: additionalMainDocuments } = useGetDocuments(
    additionalConditionallyRequiredIds,
    (formDataDocuments as TDocumentsInStore).documents.additionalMainDocuments || [],
    isLegalResident && isReidentification
      ? { [WebDocName.accountManagementAuthorizedPersons]: true }
      : {},
    applicationId
  );
  const { documents: assetManagerDocuments } = useGetDocuments(
    assetManagersDocumentsIds,
    (formDataDocuments as TDocumentsInStore).documents.assetManagersDocuments || []
  );
  const { documents: mainDocuments } = useGetDocuments(
    mainConditionallyRequiredIds,
    (formDataDocuments as TDocumentsInStore).documents.mainDocuments || []
  );

  const pendingUboAssignmentRef = useRef<{
    idDictWebDocument: number;
    ownerId: string;
    knownIdDocs: Set<number>;
  } | null>(null);

  const storeDocuments = [
    ...(additionalOptionalDocuments || []),
    ...(additionalMainDocuments || []),
    ...(assetManagerDocuments || []),
    ...(mainDocuments || []),
  ]
    .map((doc) => ({
      ...doc,
      docStatus: !doc.docStatus ? DocStatusEnum.NOT_VERIFIED : doc.docStatus,
    }))
    .filter((doc) => doc.idDoc !== null);

  const documentsToSign = useDocumentsToSign([
    ...mainDocuments,
    ...assetManagerDocuments,
    ...additionalOptionalDocuments,
    ...additionalMainDocuments,
  ]);

  const showAssetManagers =
    isLegalResident &&
    (formDataDocuments as TDocumentsInStore)?.documents?.assetManagersDocuments?.length > 0 &&
    !isAssetManager;

  useEffect(() => {
    const pending = pendingUboAssignmentRef.current;
    if (!pending || applicationId == null) return;

    const rawAdditionalMain =
      (formDataDocuments as TDocumentsInStore)?.documents?.additionalMainDocuments || [];

    const newlyAppearedDocs = rawAdditionalMain.filter(
      (d): d is TDocument & { idDoc: number } =>
        d.idDictWebDocument === pending.idDictWebDocument &&
        d.idDoc != null &&
        !pending.knownIdDocs.has(d.idDoc)
    );

    const [newDoc, ...rest] = newlyAppearedDocs;

    if (newDoc && rest.length === 0) {
      setUboOwnerMapEntry(applicationId, pending.idDictWebDocument, newDoc.idDoc, pending.ownerId);
      pendingUboAssignmentRef.current = null;
    } else if (newlyAppearedDocs.length > 1) {
      pendingUboAssignmentRef.current = null;
    }
  }, [formDataDocuments, applicationId]);

  const attachDocument = async (blockId: number, document: TDocument, files: File[]) => {
    const idDictWebDocument = document.idDictWebDocument;
    const currentDocuments = blockId === 2 ? additionalMainDocuments : additionalOptionalDocuments;
    const index = currentDocuments.findIndex((doc) =>
      document.id ? doc.id === document.id : doc.idDictWebDocument === idDictWebDocument
    );

    const signatureFile = files.find((file: File) => file.type === P7S_FILE_TYPES);
    const documentFile = files.find(
      (file: File) => file.lastModified !== signatureFile?.lastModified
    );

    if ((documentFile?.size || 0) > MAX_FILE_SIZE || (signatureFile?.size || 0) > MAX_FILE_SIZE) {
      dispatch(setIdentificationError({ errorMessage: errorMessages.FILE_TOO_LARGE }));
      return;
    }

    if (!ALLOWED_FILE_TYPES.includes(documentFile?.type || '')) {
      dispatch(setIdentificationError({ errorMessage: errorMessages.INVALID_FILE_FORMAT }));
      return;
    }

    const requestData = {
      'doc-type': currentDocuments[index]?.idDictWebDocument as number,
      document: documentFile,
      signature: signatureFile,
    };

    if (document.ownerId && blockId === 2) {
      const rawAdditionalMain =
        (formDataDocuments as TDocumentsInStore)?.documents?.additionalMainDocuments || [];
      const knownIdDocs = new Set(
        rawAdditionalMain
          .filter((d) => d.idDictWebDocument === idDictWebDocument && d.idDoc != null)
          .map((d) => d.idDoc as number)
      );
      pendingUboAssignmentRef.current = {
        idDictWebDocument,
        ownerId: document.ownerId,
        knownIdDocs,
      };
    }

    try {
      setLoadingDocId({ blockId, index });
      if (isAssetManager) {
        await dispatch(
          addDocumentAssetManager({
            managerId: tokenData?.managerId,
            applicationId: tokenData?.appId,
            data: { requestData, blockId },
          })
        ).unwrap();
      } else {
        await dispatch(addDocument({ applicationId, data: { requestData, blockId } })).unwrap();
      }
    } catch (error: unknown) {
      pendingUboAssignmentRef.current = null;
      const axiosError = error as AxiosError;
      if (axiosError.status) {
        if (axiosError.status === HTTP_ERROR_STATUS.BAD_REQUEST) {
          if (
            ERROR_CODES.SIGNATURE_VALIDATION_ERROR.toString().includes(
              (error as AxiosResponse).data?.errorCode
            )
          ) {
            dispatch(setIdentificationError((error as AxiosResponse).data?.message));

            return;
          }
        }
      } else if (axiosError.message) {
        dispatch(setIdentificationError({ errorMessage: axiosError.message }));
      } else {
        dispatch(setIdentificationError({ errorMessage: errorMessages.SERVICE_UNAVAILABLE }));
      }
    } finally {
      setLoadingDocId(null);
    }
  };

  const fetchPreview = async (selectedDocId: number | string) => {
    const selectedDoc = [
      ...mainDocuments,
      ...additionalMainDocuments,
      ...additionalOptionalDocuments,
      ...assetManagerDocuments,
    ].find((document) => document.idDoc === selectedDocId);

    if (selectedDoc && selectedDoc.idDoc) {
      try {
        if (isAssetManager) {
          await dispatch(
            previewAssetManagerDocument({
              managerId: tokenData?.managerId,
              applicationId: tokenData?.appId,
              idDoc: selectedDoc.idDoc,
            })
          ).unwrap();
        } else {
          await dispatch(previewDocument({ applicationId, idDoc: selectedDoc.idDoc })).unwrap();
        }
      } catch {
        dispatch(setIdentificationError({ errorMessage: errorMessages.SERVICE_UNAVAILABLE }));
      }
    } else {
      console.error('Document not found or idDoc missing');
    }
  };

  const confirmDeleteDocument = useCallback(() => {
    if (currentDocId) {
      if (isAssetManager) {
        dispatch(
          deleteDocumentAssetManager({
            applicationId,
            blockId: currentDocId.blockId,
            managerId: tokenData?.managerId,
            idDoc: currentDocId.idDoc,
          })
        ).then(() => {
          closeDeleteModal();
        });
      } else {
        dispatch(
          deleteDocument({
            applicationId,
            idDoc: currentDocId.idDoc,
            blockId: currentDocId.blockId,
          })
        ).then(() => {
          closeDeleteModal();
        });
      }
    }
  }, [currentDocId?.idDoc, isAssetManager]);

  const closeDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
    setCurrentDocId(null);
  }, []);

  return (
    <Box sx={[commonStyles.columnFlexContainer, { gap: 3 }] as SxProps}>
      <DocumentsSection
        headerText="Перегляньте автоматично сформовані документи"
        documents={mainDocuments}
        fetchPreview={fetchPreview}
        blockId={1}
        showSvgEyeOpen
      />
      <DocumentsSection
        headerText="Додайте обов'язкові документи"
        documents={additionalMainDocuments}
        attachDocument={attachDocument}
        fetchPreview={fetchPreview}
        setShowDeleteModal={setShowDeleteModal}
        setCurrentDocId={setCurrentDocId}
        blockId={2}
        loadingDocId={loadingDocId}
        showSvgEyeOpen={false}
        isEditable
      />
      <DocumentsSection
        headerText="Додайте додаткові документи"
        documents={additionalOptionalDocuments}
        attachDocument={attachDocument}
        fetchPreview={fetchPreview}
        setShowDeleteModal={setShowDeleteModal}
        setCurrentDocId={setCurrentDocId}
        loadingDocId={loadingDocId}
        blockId={3}
        showSvgEyeOpen={false}
        isEditable
      />

      {showAssetManagers && (
        <DocumentsSection
          headerText="Документи розпорядників"
          documents={assetManagerDocuments}
          attachDocument={attachDocument}
          fetchPreview={fetchPreview}
          blockId={4}
          showSection={showAssetManagers}
          showSvgEyeOpen
        />
      )}

      <DeleteModal
        closeDeleteModal={closeDeleteModal}
        confirmDelete={confirmDeleteDocument}
        showDeleteModal={showDeleteModal}
        title="Ви збираєтеся видалити цей документ. Після видалення файл не можна буде відновити."
      />

      <SignDocuments
        documentsToSign={documentsToSign}
        storeDocuments={storeDocuments}
        fetchPreview={fetchPreview}
        mainDocuments={mainDocuments}
        additionalMainDocuments={additionalMainDocuments}
        applicationId={applicationId}
      />
    </Box>
  );
};

export default Documents;
